import { sum } from 'lodash'
import {
  addDaysToDate,
  getDayNumberOfYear,
} from '../components/common/util/date-util'
import {
  DayProfileType,
  ScheduleFragment,
  ScheduleHourlyProfileData,
} from '../generated/graphql'
import { hoursInYear } from '../util/hours-in-year'

const getHourFactor = (
  hourlyDatas: ScheduleHourlyProfileData[] | undefined | null,
  hour: number,
  dayProfile: DayProfileType,
): number => {
  let factor = 0

  switch (dayProfile) {
    case DayProfileType.Always:
      factor = 1
      break
    case DayProfileType.Normal:
      factor =
        hourlyDatas?.find((d) => d.profileType === DayProfileType.Normal)
          ?.values[hour] ?? 0
      break
    case DayProfileType.Exception:
      factor =
        hourlyDatas?.find((d) => d.profileType === DayProfileType.Exception)
          ?.values[hour] ?? 0
      break
    case DayProfileType.Off:
      factor = 0
      break
    default:
      break
  }

  return factor
}

export function calculateHourSerieFactors(
  schedule: ScheduleFragment,
  year: number,
): number[] {
  const factors = Array.from(Array(hoursInYear).fill(0)) as number[]
  let currentDate = new Date(year, 0, 1)

  // Exceptional usage
  let exceptionStart = 367
  let exceptionEnd = -1
  if (
    schedule.exceptionData?.inUse &&
    schedule.exceptionData.startAt &&
    schedule.exceptionData.endAt
  ) {
    exceptionStart = getDayNumberOfYear(
      new Date(schedule.exceptionData.startAt.replace('Z', '')),
    )

    // Ignore local time offset (dates in the database are in UTC format)
    const exceptionEndDate = new Date(schedule.exceptionData.endAt.slice(0, -1))

    exceptionEnd = getDayNumberOfYear(exceptionEndDate)
  }

  let factorIndex = 0
  for (let i = 1; i < 366; i++) {
    const monthFactor =
      schedule.monthlyProfile?.multipliers[currentDate.getMonth()] ?? 0

    let dayProfile = DayProfileType.Off
    // Check exceptional usage
    if (i >= exceptionStart && i <= exceptionEnd)
      dayProfile = schedule.exceptionData?.profileType ?? DayProfileType.Off
    else
      dayProfile =
        schedule.weeklyProfile?.profileTypes[(currentDate.getDay() + 6) % 7] ??
        DayProfileType.Off

    for (let j = 0; j < 24; j++) {
      const hourFactor = getHourFactor(schedule.hourlyProfiles, j, dayProfile)
      factors[factorIndex] = monthFactor * hourFactor
      factorIndex += 1
    }

    currentDate = addDaysToDate(currentDate, 1)
  }

  return factors
}

export function calculateHourSerieFactorsSum(
  schedule: ScheduleFragment,
  year: number,
): number {
  return sum(calculateHourSerieFactors(schedule, year))
}
